import { useState } from "react";
import { CheckCheck, Loader2, Mail, Printer } from "lucide-react";
import { toast } from "sonner";
import { api, formatPrice } from "@/lib/api";

const esc = (v) => String(v ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));

function recapHtml({ association: a, orders, totals }) {
  const today = new Date().toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  const totalRows = totals.map((t) => `<tr><td>${esc(t.label)}</td><td class="n">${t.quantity}</td></tr>`).join("");
  const orderRows = orders.map((o) => `<tr><td class="box">☐</td><td><b>${esc(o.name || "Client")}</b><br>${esc(o.email)}${o.phone ? `<br>${esc(o.phone)}` : ""}<br><small>Commande du ${new Date(o.created_at).toLocaleDateString("fr-FR")}</small></td><td>${o.items.map((i) => `${i.quantity} × ${esc(i.label)}`).join("<br>")}</td><td class="n">${formatPrice(Math.round(o.amount * 100))}</td></tr>`).join("");
  return `<!doctype html><html lang="fr"><head><meta charset="utf-8"><title>Récapitulatif ${esc(a.name)}</title><style>body{font-family:Arial,sans-serif;color:#2C2218;padding:16px}h1{font-family:Georgia,serif;margin:0 0 4px}h2{font-family:Georgia,serif;margin:24px 0 8px;font-size:18px}p{margin:2px 0;font-size:13px}table{width:100%;border-collapse:collapse;font-size:12px}td,th{border-bottom:1px solid #E6DCCB;padding:8px;text-align:left;vertical-align:top}th{color:#B8730A;font-size:10px;text-transform:uppercase}.n{text-align:right;white-space:nowrap}.box{font-size:20px;width:28px}tr{page-break-inside:avoid}</style></head><body><h1>Pétales de Miel — ${esc(a.name)} (${esc(a.code)})</h1><p>Distribution — récapitulatif édité le ${today}</p>${a.pickup_address ? `<p>Lieu de retrait : ${esc(a.pickup_address)}</p>` : ""}${a.next_distribution_label ? `<p>Prochaine distribution : ${esc(a.next_distribution_label)}</p>` : ""}<p>${orders.length} commande(s) à remettre</p><h2>Produits à préparer</h2><table><tr><th>Produit</th><th class="n">Quantité</th></tr>${totalRows}</table><h2>Détail par client</h2><table><tr><th>Remis</th><th>Client</th><th>Articles</th><th class="n">Montant</th></tr>${orderRows}</table><script>window.onload=function(){setTimeout(function(){window.print()},300)}</script></body></html>`;
}

export default function AssociationRecap({ association, onChanged }) {
  const [busy, setBusy] = useState("");
  const pending = association.pending_count || 0;

  const print = async () => {
    const w = window.open("", "_blank");
    setBusy("print");
    try {
      const { data } = await api.get(`/admin/associations/${association.association_id}/recap`);
      w.document.write(recapHtml(data));
      w.document.close();
    } catch {
      w?.close();
      toast.error("Impossible de générer le récapitulatif");
    } finally {
      setBusy("");
    }
  };

  const sendMail = async () => {
    if (!window.confirm(`Envoyer le récapitulatif à ${association.email} ?`)) return;
    setBusy("mail");
    try {
      const { data } = await api.post(`/admin/associations/${association.association_id}/send-recap`);
      toast.success(data.message);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" && !d.startsWith("Failed") ? d : "L'e-mail n'a pas pu être envoyé");
    } finally {
      setBusy("");
    }
  };

  const markAll = async () => {
    if (!window.confirm(`Marquer les ${pending} commande(s) de « ${association.name} » comme livrées ?`)) return;
    setBusy("mark");
    try {
      const { data } = await api.post(`/admin/associations/${association.association_id}/mark-delivered`);
      toast.success(`${data.updated} commande(s) marquée(s) livrée(s)`);
      onChanged();
    } catch {
      toast.error("Action impossible");
    } finally {
      setBusy("");
    }
  };

  const btn = "flex items-center gap-1.5 rounded-full border px-4 py-1.5 text-xs font-medium transition-colors disabled:opacity-50";
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span data-testid={`association-pending-${association.code}`} className="text-xs text-bark-soft">{pending} à remettre</span>
      <button data-testid={`association-recap-${association.code}`} onClick={print} disabled={!pending || busy} className={`${btn} border-amber-deep/40 text-amber-deep hover:bg-amber-50`}>
        {busy === "print" ? <Loader2 size={13} className="animate-spin" /> : <Printer size={13} />} Récapitulatif
      </button>
      <button data-testid={`association-send-recap-${association.code}`} onClick={sendMail} disabled={!pending || !association.email || busy} title={association.email ? "" : "Ajoutez l'e-mail de l'association"} className={`${btn} border-amber-deep/40 text-amber-deep hover:bg-amber-50`}>
        {busy === "mail" ? <Loader2 size={13} className="animate-spin" /> : <Mail size={13} />} Envoyer par e-mail
      </button>
      <button data-testid={`association-mark-delivered-${association.code}`} onClick={markAll} disabled={!pending || busy} className={`${btn} border-sage/50 text-sage hover:bg-sage/10`}>
        {busy === "mark" ? <Loader2 size={13} className="animate-spin" /> : <CheckCheck size={13} />} Tout marquer livré
      </button>
    </div>
  );
}
